import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../utils/api';
import { PresthygCard, PresthygStatCard, PresthygButton, PresthygBadge, PresthygPageHeader, PresthygEmptyState } from '../components/presthyg';
import { FileText, Users, Package, ClipboardList, Plus, ArrowRight, Upload, Layers, MapPin, Sparkles } from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState({ plans: 0, clients: 0, products: 0, zones: 0 });
  const [recentPlans, setRecentPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      const [plansRes, clientsRes, productsRes, zonesRes] = await Promise.all([
        api.get('/plans'),
        api.get('/clients'),
        api.get('/products'),
        api.get('/zones'),
      ]);
      setStats({
        plans: plansRes.data.length,
        clients: clientsRes.data.length,
        products: productsRes.data.length,
        zones: zonesRes.data.length,
      });
      setRecentPlans(plansRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-3 border-t-transparent animate-spin" style={{ borderColor: 'var(--ph-teal)', borderTopColor: 'transparent', borderRadius: '50%', borderWidth: '3px' }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="dashboard-page">
        {/* Hero welcome */}
        <div
          className="mb-8 px-8 py-8 relative overflow-hidden"
          style={{
            borderRadius: 'var(--ph-radius-xxl)',
            background: 'linear-gradient(135deg, #0E2D43 0%, #123E58 42%, #167786 100%)',
          }}
        >
          <div className="relative z-10">
            <h1
              className="text-2xl font-bold mb-2"
              style={{ color: '#fff', fontFamily: 'var(--font-display)' }}
            >
              Bienvenue sur PREST'HYG
            </h1>
            <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Gerez vos plans HACCP, produits et methodes en toute simplicite.
            </p>
            <div className="flex gap-3">
              <Link to="/plan-builder">
                <PresthygButton variant="teal" data-testid="create-new-plan-button">
                  <Plus size={16} /> Creer un plan
                </PresthygButton>
              </Link>
              <Link to="/plan-ai">
                <PresthygButton variant="secondary" data-testid="create-ai-plan-button">
                  <Sparkles size={16} /> Creer avec IA
                </PresthygButton>
              </Link>
              <Link to="/import">
                <PresthygButton variant="secondary">
                  <Upload size={16} /> Import Excel
                </PresthygButton>
              </Link>
            </div>
          </div>
          {/* Decorative circles */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full" style={{ background: 'rgba(22,119,134,0.2)' }} />
          <div className="absolute -bottom-8 -right-4 w-32 h-32 rounded-full" style={{ background: 'rgba(22,119,134,0.1)' }} />
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-4 gap-5 mb-8">
          <PresthygStatCard
            data-testid="stat-plans-totaux"
            label="Plans HACCP"
            value={stats.plans}
            icon={ClipboardList}
            accentColor="var(--ph-teal)"
          />
          <PresthygStatCard
            data-testid="stat-clients-totaux"
            label="Clients"
            value={stats.clients}
            icon={Users}
            accentColor="var(--ph-navy)"
          />
          <PresthygStatCard
            data-testid="stat-produits"
            label="Produits"
            value={stats.products}
            icon={Package}
            accentColor="var(--ph-terracotta)"
          />
          <PresthygStatCard
            data-testid="stat-zones"
            label="Zones"
            value={stats.zones}
            icon={MapPin}
            accentColor="var(--ph-orange)"
          />
        </div>

        {/* Recent Plans + Quick Actions */}
        <div className="grid grid-cols-3 gap-6">
          {/* Recent Plans */}
          <div className="col-span-2">
            <PresthygCard className="overflow-hidden">
              <div className="px-6 pt-6 pb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-display)' }}>
                    Plans recents
                  </h2>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--ph-slate500)' }}>
                    Derniers plans crees ou modifies
                  </p>
                </div>
                <Link to="/plans">
                  <PresthygButton variant="ghost" className="text-xs">
                    Voir tout <ArrowRight size={14} />
                  </PresthygButton>
                </Link>
              </div>

              {recentPlans.length === 0 ? (
                <PresthygEmptyState
                  icon={ClipboardList}
                  title="Aucun plan HACCP"
                  description="Creez votre premier plan de nettoyage et desinfection."
                  action={
                    <Link to="/plan-builder">
                      <PresthygButton variant="teal"><Plus size={16} /> Creer un plan</PresthygButton>
                    </Link>
                  }
                />
              ) : (
                <div className="px-6 pb-6">
                  <div className="space-y-2">
                    {recentPlans.map((plan) => (
                      <Link
                        key={plan.id}
                        to="/plans"
                        data-testid={`plan-${plan.id}`}
                        className="flex items-center justify-between px-4 py-3.5 transition-all hover:translate-x-0.5"
                        style={{
                          borderRadius: 'var(--ph-radius-md)',
                          border: '1px solid var(--ph-slate200)',
                          backgroundColor: 'var(--ph-white)',
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center" style={{ borderRadius: 'var(--ph-radius-sm)', backgroundColor: 'var(--ph-slate100)' }}>
                            <FileText size={16} style={{ color: 'var(--ph-teal)' }} />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--ph-navy)' }}>
                              {plan.plan_name}
                            </h3>
                            <p className="text-xs" style={{ color: 'var(--ph-slate500)' }}>
                              {new Date(plan.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <PresthygBadge status={plan.status === 'draft' ? 'draft' : 'validated'}>
                          {plan.status === 'draft' ? 'Brouillon' : 'Valide'}
                        </PresthygBadge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </PresthygCard>
          </div>

          {/* Quick Actions */}
          <div className="space-y-5">
            <PresthygCard className="p-6">
              <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-display)' }}>
                Actions rapides
              </h3>
              <div className="space-y-2.5">
                {[
                  { to: '/plan-builder', label: 'Nouveau plan HACCP', icon: Plus, color: 'var(--ph-teal)' },
                  { to: '/import', label: 'Import Excel', icon: Upload, color: 'var(--ph-orange)' },
                  { to: '/clients', label: 'Gerer les clients', icon: Users, color: 'var(--ph-navy)' },
                  { to: '/products', label: 'Bibliotheque produits', icon: Package, color: 'var(--ph-terracotta)' },
                  { to: '/methods', label: 'Methodes de nettoyage', icon: Layers, color: 'var(--ph-slate700)' },
                ].map((action) => (
                  <Link
                    key={action.to}
                    to={action.to}
                    className="flex items-center gap-3 px-3 py-2.5 transition-all hover:translate-x-0.5"
                    style={{
                      borderRadius: 'var(--ph-radius-sm)',
                      border: '1px solid var(--ph-slate200)',
                    }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center" style={{ borderRadius: 'var(--ph-radius-sm)', backgroundColor: `${action.color}10` }}>
                      <action.icon size={15} style={{ color: action.color }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--ph-navy)' }}>{action.label}</span>
                  </Link>
                ))}
              </div>
            </PresthygCard>

            <PresthygCard className="p-5" style={{ background: 'linear-gradient(135deg, #167786 0%, #0E2D43 100%)' }}>
              <h3 className="text-sm font-bold mb-1" style={{ color: '#fff', fontFamily: 'var(--font-display)' }}>
                Besoin d'aide ?
              </h3>
              <p className="text-xs mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Importez un fichier Excel pour generer automatiquement un plan HACCP complet.
              </p>
              <Link to="/import">
                <PresthygButton variant="secondary" className="text-xs">
                  Commencer l'import
                </PresthygButton>
              </Link>
            </PresthygCard>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
